import { cur } from '@/lib/currency';
import { useState, useEffect, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Clock, AlertTriangle, FileText, RefreshCw, Phone, Mail, MessageCircle, Search } from 'lucide-react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';

const BUCKET_META = [
  { key: 'current',  label: 'Not Due Yet', short: 'Current', color: 'from-emerald-500 to-teal-600', pale: 'bg-success/10 text-success', border: 'border-success/30' },
  { key: '0_30',     label: '1-30 Days',   short: '0-30',   color: 'from-amber-500  to-yellow-600',  pale: 'bg-warning/10   text-warning',   border: 'border-warning/30' },
  { key: '31_60',    label: '31-60 Days',  short: '31-60',  color: 'from-orange-500 to-red-500',    pale: 'bg-orange-50  text-orange-700',  border: 'border-orange-200' },
  { key: '61_90',    label: '61-90 Days',  short: '61-90',  color: 'from-rose-500  to-red-600',     pale: 'bg-error/10    text-error',    border: 'border-error/30' },
  { key: '90_plus',  label: '90+ Days',    short: '90+',    color: 'from-red-600   to-red-800',     pale: 'bg-error/20    text-error',    border: 'border-error/30' },
];

const fmt = (n) => (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CreditInvoiceAgingPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState('');
  const [activeBucket, setActiveBucket] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/branches').then(r => setBranches(r.data || [])).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/invoices/aging-report', { params: branchId ? { branch_id: branchId } : {} });
      setData(res.data);
    } catch { toast.error('Failed to load aging report'); }
    setLoading(false);
  }, [branchId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const sendReminder = (row, via) => {
    const msg = `Hi ${row.customer_name},\n\nThis is a friendly reminder that invoice ${row.invoice_number || row.invoice_number_ref || ''} for ${cur()} ${fmt(row.outstanding)} is ${row.days_overdue >= 0 ? `${row.days_overdue} days overdue` : 'coming due soon'}.\n\nPlease arrange payment at your earliest convenience.\n\nThank you!`;
    if (via === 'whatsapp') {
      const phone = (row.phone || '').replace(/[^\d]/g, '');
      if (!phone) { toast.error('No phone number on file'); return; }
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
    } else if (via === 'email') {
      const email = row.email || '';
      if (!email) { toast.error('No email on file'); return; }
      window.open(`mailto:${email}?subject=${encodeURIComponent('Payment Reminder')}&body=${encodeURIComponent(msg)}`, '_blank');
    }
  };

  const customerList = (data?.by_customer || []).filter(c =>
    !search || (c.customer_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const allBucketRows = activeBucket
    ? (data?.buckets?.[activeBucket] || [])
    : Object.values(data?.buckets || {}).flat();
  const filteredRows = allBucketRows.filter(r =>
    !search || (r.customer_name || '').toLowerCase().includes(search.toLowerCase()) || (r.invoice_number || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <DashboardLayout>
      <div className="space-y-5 max-w-[1400px] mx-auto" data-testid="aging-report-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-rose-500 to-red-600 flex items-center justify-center shadow-lg shadow-rose-200/50">
              <Clock size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">Credit Invoices Aging</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Receivables overdue analysis — 0-30 / 31-60 / 61-90 / 90+ days</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={branchId || 'all'} onValueChange={v => setBranchId(v === 'all' ? '' : v)}>
              <SelectTrigger className="w-[180px] h-9 text-xs" data-testid="branch-filter"><SelectValue placeholder="All Branches" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={fetchData} data-testid="refresh-btn"><RefreshCw size={14} className="mr-1" />Refresh</Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center min-h-[40vh]">
            <div className="w-8 h-8 border-4 border-error border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !data?.by_customer?.length ? (
          <Card><CardContent className="p-12 text-center text-muted-foreground text-sm">No outstanding credit invoices. 🎉</CardContent></Card>
        ) : (
          <>
            {/* Bucket KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="bucket-kpis">
              {BUCKET_META.map(b => {
                const amount = data.totals?.[b.key] || 0;
                const count = (data.buckets?.[b.key] || []).length;
                const active = activeBucket === b.key;
                return (
                  <button
                    key={b.key}
                    onClick={() => setActiveBucket(active ? null : b.key)}
                    data-testid={`bucket-${b.key}`}
                    className={`text-left rounded-xl p-4 bg-gradient-to-br ${b.color} text-white shadow-md transition-all ${active ? 'ring-4 ring-offset-2 ring-stone-900 scale-[1.02]' : 'hover:scale-[1.01]'}`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] uppercase tracking-wider text-white/80 font-medium">{b.label}</p>
                      <span className="text-[10px] bg-white/20 px-1.5 py-0.5 rounded-full">{count}</span>
                    </div>
                    <p className="text-lg sm:text-xl font-bold mt-1 tabular-nums">{cur()} {fmt(amount)}</p>
                  </button>
                );
              })}
            </div>

            {/* Search */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1 max-w-md">
                <Search size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
                <Input
                  placeholder="Search by customer or invoice #"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-8 h-9 text-sm"
                  data-testid="search-input"
                />
              </div>
              {activeBucket && (
                <Button size="sm" variant="ghost" onClick={() => setActiveBucket(null)} data-testid="clear-bucket">
                  Clear bucket filter
                </Button>
              )}
              <div className="ml-auto text-sm text-muted-foreground">
                Grand total outstanding: <span className="font-bold text-foreground">{cur()} {fmt(data.grand_total)}</span>
              </div>
            </div>

            {/* Summary by Customer */}
            <Card>
              <div className="px-4 py-2.5 border-b border-border text-sm font-semibold text-foreground">
                Summary by Customer ({customerList.length})
              </div>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-xs" data-testid="customer-summary-table">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Customer</th>
                      {BUCKET_META.map(b => <th key={b.key} className="text-right px-3 py-2 font-semibold text-muted-foreground">{b.short}</th>)}
                      <th className="text-right px-3 py-2 font-semibold text-muted-foreground bg-error/10">Total</th>
                      <th className="text-center px-3 py-2 font-semibold text-muted-foreground">Oldest</th>
                      <th className="text-center px-3 py-2 font-semibold text-muted-foreground">Contact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerList.map(c => (
                      <tr key={c.customer_id} className="border-b border-border hover:bg-muted/50" data-testid={`customer-row-${c.customer_id}`}>
                        <td className="px-3 py-2.5 font-medium text-foreground">{c.customer_name}</td>
                        {BUCKET_META.map(b => (
                          <td key={b.key} className={`px-3 py-2.5 text-right tabular-nums ${(c[b.key] || 0) > 0 ? b.pale : 'text-muted-foreground'}`}>
                            {(c[b.key] || 0) > 0 ? fmt(c[b.key]) : '—'}
                          </td>
                        ))}
                        <td className="px-3 py-2.5 text-right tabular-nums font-bold bg-error/10 text-error">{cur()} {fmt(c.total)}</td>
                        <td className="px-3 py-2.5 text-center">
                          {c.oldest_days > 0 ? (
                            <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${c.oldest_days > 90 ? 'bg-error/20 text-error' : c.oldest_days > 60 ? 'bg-orange-100 text-orange-800' : c.oldest_days > 30 ? 'bg-warning/20 text-warning' : 'bg-warning/20 text-yellow-800'}`}>
                              <AlertTriangle size={10} /> {c.oldest_days}d
                            </span>
                          ) : <span className="text-muted-foreground">—</span>}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {c.phone && (
                              <button
                                onClick={() => sendReminder({ ...c, outstanding: c.total, days_overdue: c.oldest_days }, 'whatsapp')}
                                className="p-1.5 rounded-md text-success hover:bg-success/10"
                                title="Send WhatsApp reminder"
                                data-testid={`whatsapp-${c.customer_id}`}
                              >
                                <MessageCircle size={13} />
                              </button>
                            )}
                            {c.email && (
                              <button
                                onClick={() => sendReminder({ ...c, outstanding: c.total, days_overdue: c.oldest_days }, 'email')}
                                className="p-1.5 rounded-md text-info hover:bg-info/10"
                                title="Send Email reminder"
                                data-testid={`email-${c.customer_id}`}
                              >
                                <Mail size={13} />
                              </button>
                            )}
                            {!c.phone && !c.email && <span className="text-muted-foreground text-[10px]">—</span>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Invoice detail */}
            <Card>
              <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
                <span className="text-sm font-semibold text-foreground">
                  {activeBucket ? `Invoices in ${BUCKET_META.find(b => b.key === activeBucket)?.label}` : 'All outstanding invoices'}
                  <span className="text-muted-foreground font-normal ml-2">({filteredRows.length})</span>
                </span>
              </div>
              <CardContent className="p-0 overflow-x-auto">
                <table className="w-full text-xs" data-testid="invoice-detail-table">
                  <thead className="bg-muted/50 border-b border-border">
                    <tr>
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Invoice #</th>
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Customer</th>
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Date</th>
                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Due</th>
                      <th className="text-right px-3 py-2 font-semibold text-muted-foreground">Total</th>
                      <th className="text-right px-3 py-2 font-semibold text-muted-foreground">Paid</th>
                      <th className="text-right px-3 py-2 font-semibold text-muted-foreground">Outstanding</th>
                      <th className="text-center px-3 py-2 font-semibold text-muted-foreground">Age</th>
                      <th className="text-center px-3 py-2 font-semibold text-muted-foreground">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map(r => {
                      const bucketMeta = BUCKET_META.find(b => b.key === r.bucket);
                      return (
                        <tr key={r.invoice_id} className="border-b border-border hover:bg-muted/50">
                          <td className="px-3 py-2 font-mono font-medium text-foreground">{r.invoice_number || r.invoice_number_ref || '-'}</td>
                          <td className="px-3 py-2">{r.customer_name || '-'}</td>
                          <td className="px-3 py-2 text-muted-foreground">{r.date ? new Date(r.date).toLocaleDateString() : '-'}</td>
                          <td className="px-3 py-2 text-muted-foreground">{r.due_date ? new Date(r.due_date).toLocaleDateString() : <span className="text-muted-foreground italic">none</span>}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmt(r.total)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-success">{fmt(r.paid)}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-bold text-error">{fmt(r.outstanding)}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${bucketMeta?.pale || 'bg-muted text-muted-foreground'}`}>
                              {r.days_overdue < 0 ? `${-r.days_overdue}d left` : `${r.days_overdue}d`}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                              {r.phone && (
                                <button
                                  onClick={() => sendReminder(r, 'whatsapp')}
                                  className="p-1 rounded text-success hover:bg-success/10"
                                  title="WhatsApp"
                                >
                                  <MessageCircle size={12} />
                                </button>
                              )}
                              {r.email && (
                                <button
                                  onClick={() => sendReminder(r, 'email')}
                                  className="p-1 rounded text-info hover:bg-info/10"
                                  title="Email"
                                >
                                  <Mail size={12} />
                                </button>
                              )}
                              {r.phone && !r.email && (
                                <a href={`tel:${r.phone}`} className="p-1 rounded text-muted-foreground hover:bg-muted" title="Call">
                                  <Phone size={12} />
                                </a>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
